import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useHousehold } from '../context/HouseholdContext';
import { useTheme } from '../context/ThemeContext';
import Logo from '../components/Logo';
import CoachTwin from '../components/CoachTwin';
import SkillRadar from '../components/SkillRadar';
import LoadGauge from '../components/LoadGauge';
import MatchCard from '../components/MatchCard';
import CreditBalance from '../components/CreditBalance';
import FamilyLeaderboard from '../components/FamilyLeaderboard';
import TodaysClipCard from '../components/TodaysClipCard';
import InspiredByYouCard from '../components/InspiredByYouCard';
import WebPushPrompt from '../components/WebPushPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Athlete Dashboard v2.
 *
 * Layout (mobile-first, fluid to wide):
 *   - Sticky top bar with logo, household switcher, theme toggle, sign-out.
 *   - Athlete header + 3 hero numbers (Form, Bat speed, Streak).
 *   - 2-column on desktop: Coach Twin (left), Skill Radar + Load Gauge (right).
 *   - Quick actions row (Record / Journal / Plan / Coach).
 *   - Recent sessions list with a "Make match card" affordance.
 *   - Cohort percentile chips.
 *
 * Data flow: pulls /providers/sessions, /load/{id}, /cohort/{id}/percentiles
 * in parallel. Everything else is lazy.
 */
export default function AthleteDashboard() {
  const { token, logout } = useAuth();
  const { household, athletes, activeAthlete, setActiveAthleteId, loading } = useHousehold();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [load, setLoad] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [cardForSession, setCardForSession] = useState(null);

  useEffect(() => {
    if (!loading && (!athletes || athletes.length === 0)) {
      navigate('/onboarding', { replace: true });
    }
  }, [loading, athletes, navigate]);

  useEffect(() => {
    if (!activeAthlete) return;
    const id = activeAthlete.id;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.allSettled([
      axios.get(`${API}/providers/sessions`, { params: { athlete_id: id, limit: 12 }, headers }),
      axios.get(`${API}/load/${id}`, { headers }),
      axios.get(`${API}/cohort/${id}/percentiles`, { headers }),
    ]).then(([s, l, c]) => {
      setSessions(s.status === 'fulfilled' ? (s.value.data.sessions || []) : []);
      setLoad(l.status === 'fulfilled' ? l.value.data : null);
      setCohort(c.status === 'fulfilled' ? c.value.data : null);
    });
  }, [activeAthlete, token]);

  const hero = useMemo(() => deriveHero(sessions), [sessions]);

  if (loading) return <FullScreenLoader theme={theme} />;
  if (!activeAthlete) return null;

  const cohortFor = (key) => cohort?.metrics?.[key];

  // Build a metrics object for the radar from sessions + load.
  const radarMetrics = useMemo(() => {
    const weekly = sessions.filter((s) => withinDays(s.started_at, 7));
    const speeds = weekly
      .map((s) => s.metrics_summary?.bat_speed_kmh)
      .filter(Boolean);
    const effs = weekly
      .map((s) => s.metrics_summary?.efficiency_pct)
      .filter(Boolean);
    return {
      bat_speed_kmh:    speeds.length ? avg(speeds) : null,
      efficiency_pct:   effs.length ? avg(effs) : null,
      balls_per_week:   weekly.reduce((a, s) => a + (s.total_balls || 0), 0),
      sessions_per_week: weekly.length,
      mood_avg:         null,  // wired in once journals expose mood here
      load_balance:     load?.acwr ?? null,
    };
  }, [sessions, load]);

  return (
    <div
      className="min-h-screen"
      style={{
        background: theme === 'dark'
          ? 'linear-gradient(180deg, #060818, #0a0e27)'
          : 'linear-gradient(180deg, #f8fafc, #eef2f7)',
        color: theme === 'dark' ? '#f7fafc' : '#0f172a',
      }}
    >
      <TopBar
        theme={theme}
        toggleTheme={toggleTheme}
        athletes={athletes}
        activeAthlete={activeAthlete}
        onSwitch={setActiveAthleteId}
        onLogout={() => { logout(); navigate('/'); }}
      />

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <Header athlete={activeAthlete} />

        <HeroRow hero={hero} theme={theme} />

        <CohortChips cohort={cohort} theme={theme} />

        <WebPushPrompt />

        <TodaysClipCard athleteId={activeAthlete.id} theme={theme} />

        <InspiredByYouCard athleteId={activeAthlete.id} theme={theme} />

        <div className="grid lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3">
            <CoachTwin
              athleteId={activeAthlete.id}
              athleteName={activeAthlete.display_name || activeAthlete.full_name}
              theme={theme}
            />
          </div>
          <div className="lg:col-span-2 space-y-5">
            <Card theme={theme} title="Skill radar">
              <div className="flex items-center justify-center">
                <SkillRadar metrics={radarMetrics} cohort={cohort} theme={theme} />
              </div>
              {cohort?.status === 'cohort_size_too_small' && (
                <p className="text-xs opacity-60 text-center mt-2">
                  Peer cohort growing — comparisons unlock at 5+ athletes
                </p>
              )}
            </Card>
            <Card theme={theme} title="Training load">
              <LoadGauge load={load} theme={theme} />
            </Card>
          </div>
        </div>

        <QuickActions theme={theme} navigate={navigate} />

        <FamilyLeaderboard theme={theme} />

        <SessionList
          sessions={sessions}
          theme={theme}
          onMatchCard={(id) => setCardForSession(id)}
        />
      </main>

      {cardForSession && (
        <MatchCard sessionId={cardForSession} onClose={() => setCardForSession(null)} />
      )}
    </div>
  );
}

// ---------- presentational pieces ----------

function TopBar({ theme, toggleTheme, athletes, activeAthlete, onSwitch, onLogout }) {
  return (
    <header
      className="sticky top-0 z-30 backdrop-blur-md"
      style={{
        background: theme === 'dark' ? 'rgba(6,8,24,0.7)' : 'rgba(255,255,255,0.7)',
        borderBottom: '1px solid rgba(0,201,167,0.15)',
      }}
    >
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
        <Logo size={28} />
        <div className="flex-1" />
        {athletes && athletes.length > 1 && (
          <select
            value={activeAthlete?.id}
            onChange={(e) => onSwitch(e.target.value)}
            className="px-3 py-1.5 rounded-lg text-sm"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(150,160,180,0.25)',
              color: 'inherit',
            }}
          >
            {athletes.map((a) => (
              <option key={a.id} value={a.id}>{a.display_name || a.full_name}</option>
            ))}
          </select>
        )}
        <CreditBalance compact />
        <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }}>
          {theme === 'dark' ? '☀' : '☾'}
        </button>
        <button onClick={onLogout} className="text-sm opacity-70 hover:opacity-100">Sign out</button>
      </div>
    </header>
  );
}

function Header({ athlete }) {
  const subtitle = [
    athlete.age_group,
    athlete.primary_role,
    athlete.batting_hand && `${cap(athlete.batting_hand)}-hand`,
    athlete.club_name,
  ].filter(Boolean).join(' · ');
  return (
    <div>
      <h1 className="text-3xl font-extrabold tracking-tight">
        {athlete.display_name || athlete.full_name}
      </h1>
      {subtitle && <p className="text-sm opacity-70 mt-1">{subtitle}</p>}
    </div>
  );
}

function HeroRow({ hero, theme }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {hero.map((n) => (
        <div
          key={n.label}
          className="rounded-2xl p-4"
          style={{
            background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
            border: '1px solid rgba(0,201,167,0.15)',
          }}
        >
          <div className="text-[10px] uppercase tracking-widest opacity-60 font-bold">{n.label}</div>
          <div className="text-3xl font-extrabold mt-1">{n.value}</div>
          <div className="text-xs opacity-70 mt-0.5">{n.hint}</div>
        </div>
      ))}
    </div>
  );
}

function CohortChips({ cohort, theme }) {
  if (!cohort || cohort.status !== 'ok') return null;
  const metric = (key, label) => {
    const m = cohort.metrics[key];
    if (!m || m.percentile == null || m.you == null) return null;
    return { key, label, pct: m.percentile, value: m.you };
  };
  const chips = [
    metric('bat_speed_kmh', 'bat speed'),
    metric('efficiency_pct', 'efficiency'),
    metric('balls_per_week', 'volume'),
    metric('sessions_per_week', 'frequency'),
  ].filter(Boolean);
  if (chips.length === 0) return null;
  return (
    <div
      className="rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3 text-sm"
      style={{
        background: theme === 'dark' ? 'rgba(0,201,167,0.06)' : 'rgba(0,201,167,0.08)',
        border: '1px solid rgba(0,201,167,0.2)',
      }}
    >
      <span className="opacity-70 text-xs uppercase tracking-widest">vs peers</span>
      {chips.map((c) => (
        <span
          key={c.key}
          className="px-2.5 py-1 rounded-full font-semibold"
          style={{
            background: c.pct >= 75 ? 'rgba(0,201,167,0.18)' :
                         c.pct >= 50 ? 'rgba(0,217,255,0.15)' :
                         'rgba(148,163,184,0.15)',
            color: c.pct >= 75 ? '#00C9A7' :
                   c.pct >= 50 ? '#00D9FF' : '#94a3b8',
          }}
        >
          {c.label}: <strong>{c.pct}th</strong>
        </span>
      ))}
    </div>
  );
}

function QuickActions({ theme, navigate }) {
  const actions = [
    { label: 'Record session', icon: '🎥', to: '/videos/upload' },
    { label: 'Journal entry',  icon: '📝', to: '/journal/new' },
    { label: 'My 7-day plan',  icon: '🗺️', to: '/practice-plan' },
    { label: 'Pro clips',      icon: '✨', to: '/clips' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {actions.map((a) => (
        <button
          key={a.label}
          onClick={() => navigate(a.to)}
          className="rounded-2xl p-4 text-left transition-all hover:-translate-y-0.5"
          style={{
            background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
            border: '1px solid rgba(0,201,167,0.15)',
          }}
        >
          <div className="text-2xl mb-1">{a.icon}</div>
          <div className="font-semibold">{a.label}</div>
        </button>
      ))}
    </div>
  );
}

function SessionList({ sessions, theme, onMatchCard }) {
  return (
    <Card theme={theme} title="Recent sessions">
      {sessions.length === 0 ? (
        <p className="text-sm opacity-70">
          No sessions yet. Link a CricClubs, CricHeroes, or str8bat account from
          settings, or upload a video to start a feedback loop.
        </p>
      ) : (
        <ul className="divide-y" style={{ borderColor: 'rgba(150,160,180,0.15)' }}>
          {sessions.slice(0, 10).map((s) => (
            <li key={s.id} className="py-3 flex items-center gap-3">
              <ProviderTag provider={s.provider} />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {s.session_type === 'match' ? 'Match' : 'Practice'}
                  {s.drill_tag && ` · ${s.drill_tag}`}
                </div>
                <div className="text-xs opacity-60 truncate">
                  {fmtDate(s.started_at)}
                  {s.total_balls ? ` · ${s.total_balls} balls` : ''}
                  {s.metrics_summary?.bat_speed_kmh
                    ? ` · ${s.metrics_summary.bat_speed_kmh} km/h`
                    : ''}
                </div>
              </div>
              <button
                onClick={() => onMatchCard(s.id)}
                className="text-xs font-semibold px-2.5 py-1 rounded-full"
                style={{
                  background: 'rgba(0,201,167,0.12)',
                  color: '#00C9A7',
                  border: '1px solid rgba(0,201,167,0.3)',
                }}
              >
                Make card
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ProviderTag({ provider }) {
  const colors = {
    str8bat: '#00C9A7', stancebeam: '#A879F0',
    cricheroes: '#FF6B35', cricclubs: '#00A651',
    manual_csv: '#94a3b8',
  };
  return (
    <span
      className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded shrink-0"
      style={{
        background: `${colors[provider] || '#94a3b8'}22`,
        color: colors[provider] || '#94a3b8',
        border: `1px solid ${colors[provider] || '#94a3b8'}55`,
      }}
    >
      {provider}
    </span>
  );
}

function Card({ title, theme, children }) {
  return (
    <section
      className="rounded-2xl p-5"
      style={{
        background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
        border: '1px solid rgba(0,201,167,0.15)',
      }}
    >
      <h2 className="text-[11px] uppercase tracking-widest opacity-60 font-bold mb-3">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function FullScreenLoader({ theme }) {
  return (
    <div className="min-h-screen flex items-center justify-center"
         style={{ background: theme === 'dark' ? '#060818' : '#f8fafc' }}>
      <Logo size={48} />
    </div>
  );
}

// ---------- helpers ----------

function withinDays(iso, days) {
  if (!iso) return false;
  const ms = Date.now() - new Date(iso).getTime();
  return ms >= 0 && ms <= days * 86400000;
}

function avg(arr) {
  return Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10;
}

function deriveHero(sessions) {
  const week = sessions.filter((s) => withinDays(s.started_at, 7));
  const speeds = week.map((s) => s.metrics_summary?.bat_speed_kmh).filter(Boolean);
  const effs = week.map((s) => s.metrics_summary?.efficiency_pct).filter(Boolean);
  const days = new Set(sessions.map((s) => new Date(s.started_at).toISOString().slice(0, 10)));
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 60; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    if (days.has(d.toISOString().slice(0, 10))) streak++;
    else if (i > 0) break;
  }
  return [
    { label: 'Form', value: effs.length ? `${Math.round(avg(effs))}` : '—', hint: '7-day efficiency' },
    { label: 'Bat speed', value: speeds.length ? `${Math.round(avg(speeds))}` : '—', hint: 'km/h avg' },
    { label: 'Streak', value: `${streak}`, hint: 'days active' },
  ];
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric',
    });
  } catch { return ''; }
}

function cap(s) { return s ? s[0].toUpperCase() + s.slice(1) : s; }
